'use client';

// ** react imports
import * as React from 'react';
import PropTypes from 'prop-types';

// ** enums imports
import { TAX_FILE_STATUS, TAX_FILE_TYPE } from '../../../enums/common';

// ** utils imports
import { cn } from '../../../lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';

export default function TaxFileDropdownFilterButton({
  align = 'right',
  onSubmissionTypeChange,
  onStatusFiltersChange,
  onReset,
  fileStatus = '',
  submissionType = 'ALL',
}) {
  // states
  const [localSubmissionType, setLocalSubmissionType] =
    React.useState(submissionType);
  const [localFileStatus, setLocalFileStatus] = React.useState(fileStatus);
  const [isOpen, setIsOpen] = React.useState(false);

  // Sync local state with props
  React.useEffect(() => {
    setLocalSubmissionType(submissionType);
    setLocalFileStatus(fileStatus);
  }, [submissionType, fileStatus]);

  // vars
  const hasActiveFilters =
    localSubmissionType !== 'ALL' || localFileStatus.length > 0;
  const taxFileStatusLabels = {
    created: 'Created',
    in_progress: 'In Progress',
    completed: 'Completed',
    submitted: 'Submitted',
    processed: 'Processed',
    archived: 'Archived',
  };

  // handlers
  const handleSubmissionTypeChange = type => {
    setLocalSubmissionType(type);
  };

  const handleStatusFilterChange = (status, checked) => {
    const statuses = localFileStatus
      ? localFileStatus
          .split(',')
          .map(s => s.trim())
          .filter(s => s)
      : [];
    if (checked) {
      // Add status if not already present
      if (!statuses.includes(status)) {
        statuses.push(status);
      }
    } else {
      // Remove status if present
      const index = statuses.indexOf(status);
      if (index > -1) {
        statuses.splice(index, 1);
      }
    }
    setLocalFileStatus(statuses.join(', '));
  };

  const handleApply = () => {
    if (onSubmissionTypeChange) {
      onSubmissionTypeChange(localSubmissionType);
    }
    if (onStatusFiltersChange) {
      onStatusFiltersChange(localFileStatus);
    }
    setIsOpen(false);
  };

  const handleClear = e => {
    e.stopPropagation();
    onReset();
    setLocalSubmissionType('ALL');
    setLocalFileStatus('');
    setIsOpen(false);
  };

  return (
    <div className="relative inline-flex z-20">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className="relative flex items-center">
          <PopoverTrigger asChild>
            <button
              className={cn(
                'btn px-2.5 bg-white dark:bg-gray-800 border-gray-200 hover:border-gray-300 dark:border-gray-700/60 dark:hover:border-gray-600 text-gray-400 dark:text-gray-500',
                hasActiveFilters
                  ? 'bg-white dark:bg-gray-800 border-violet-30 dark:border-violet-600'
                  : ''
              )}
              aria-label="Filter"
            >
              <span className="sr-only">Filter</span>
              <svg
                className="fill-current"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d="M0 3a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H1a1 1 0 0 1-1-1ZM3 8a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1ZM7 12a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2H7Z" />
              </svg>

              {hasActiveFilters && (
                <div className="absolute -top-0.5 -right-1 w-3 h-3 bg-violet-500 rounded-full"></div>
              )}
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent
          className="w-auto p-0 min-w-72"
          align={align === 'right' ? 'end' : 'start'}
        >
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase pt-1.5 pb-2 px-3">
            Filters
          </div>

          <hr className="mb-4 border-t border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-700/20" />

          <div className="px-3 pb-3">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Submission Type
            </label>
            <div className="space-y-1">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="submissionType"
                  value="ALL"
                  checked={localSubmissionType === 'ALL'}
                  onChange={() => handleSubmissionTypeChange('ALL')}
                  className="form-radio text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  All Types
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="submissionType"
                  value="VIRTUAL_INTAKE"
                  checked={localSubmissionType === TAX_FILE_TYPE.VIRTUAL_INTAKE}
                  onChange={() =>
                    handleSubmissionTypeChange(TAX_FILE_TYPE.VIRTUAL_INTAKE)
                  }
                  className="form-radio text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  Virtual Intake
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="submissionType"
                  value="IN_OFFICE"
                  checked={localSubmissionType === TAX_FILE_TYPE.IN_OFFICE}
                  onChange={() =>
                    handleSubmissionTypeChange(TAX_FILE_TYPE.IN_OFFICE)
                  }
                  className="form-radio text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  In-Office
                </span>
              </label>
            </div>
          </div>

          <div className="px-3 pb-4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Options
            </label>
            {Object.entries(TAX_FILE_STATUS).map(([key, value]) => {
              return (
                <label key={value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={(localFileStatus
                      ? localFileStatus.split(',').map(s => s.trim())
                      : []
                    ).includes(value)}
                    onChange={e => {
                      handleStatusFilterChange(value, e.target.checked);
                    }}
                    className="form-checkbox text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                    {taxFileStatusLabels[value]}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="py-2 px-3 border-t border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-700/20">
            <div className="flex items-center justify-between">
              <button
                className="btn-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-red-500 hover:text-red-600 uppercase"
                onClick={handleClear}
              >
                Clear All
              </button>
              <button
                className="btn-xs bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-700 uppercase"
                onClick={handleApply}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

TaxFileDropdownFilterButton.propTypes = {
  align: PropTypes.oneOf(['left', 'right']),
  onSubmissionTypeChange: PropTypes.func,
  onStatusFiltersChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onReset: PropTypes.func,
  fileStatus: PropTypes.string,
  submissionType: PropTypes.oneOf(['ALL', 'VIRTUAL_INTAKE', 'IN_OFFICE']),
  searchTerm: PropTypes.string,
};
